import { useCallback, useEffect, useRef, useState } from 'react';
import { listUserSessions } from '../api/userSessionsApi';
import { adaptAccountSessions } from '../utils/accountSessionRecord';

/**
 * Load a page of the signed-in user's charging history.
 *
 * Server-side pagination against `GET /api/user/sessions?limit&offset`.
 * The backend returns `{ sessions, total, limit, offset }`; the hook
 * derives `totalPages` from `total` and the requested `pageSize`.
 *
 * Deliberately NEVER falls back to local storage / device history on
 * failure — an authenticated user must not be shown a device-scoped
 * empty page just because the network hiccupped. The consumer renders
 * a retryable error state instead.
 *
 * Race safety:
 *   - Each request bumps a monotonic sequence counter. Responses that
 *     arrive after a newer request has been issued are dropped on the
 *     floor — an older page cannot overwrite a newer one.
 *   - `loading` remains true during a page change so the caller can
 *     render a skeleton without flashing the empty state.
 *
 * Page clamping:
 *   - If the caller has `page = 3` and the server reports only 12 rows
 *     with pageSize = 10, `totalPages = 2` and this hook re-issues a
 *     fetch for page 2. Never leaves the UI on an invalid page.
 */
export function useAccountSessions({ pageSize = 10, initialPage = 1 } = {}) {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPageState] = useState(Math.max(1, initialPage));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Monotonic request counter — the response guard reads this via
  // ref so callbacks aren't recreated on every re-render.
  const seqRef = useRef(0);

  const load = useCallback(
    async (targetPage) => {
      const requestSeq = ++seqRef.current;
      const p = Math.max(1, targetPage);
      const offset = (p - 1) * pageSize;

      // Preserve current page while the new one loads — the UI keeps
      // rendering the previous cards under the skeleton overlay.
      setLoading(true);
      setError(null);

      const result = await listUserSessions({ limit: pageSize, offset });

      // Drop stale responses. A user who clicked "Next" three times
      // quickly still lands on page 4, not on whichever response
      // returned last.
      if (requestSeq !== seqRef.current) return;

      if (result.ok) {
        const nextTotal = typeof result.total === 'number' ? result.total : 0;
        const totalPages = Math.max(1, Math.ceil(nextTotal / pageSize));

        // Clamp: if the caller asked for a page beyond what the
        // backend currently has, re-fetch the last valid page.
        // Guard on `nextTotal > 0` so a legitimate empty-history
        // response doesn't cause a `page = 1` loop.
        if (p > totalPages && nextTotal > 0) {
          setPageState(totalPages);
          // Fire and forget — the sequence guard ensures ordering.
          load(totalPages);
          return;
        }

        setSessions(adaptAccountSessions(result.sessions));
        setTotal(nextTotal);
        setPageState(p);
        setLoading(false);
      } else {
        setError({
          code: result.error,
          message: result.message,
          status: result.status,
        });
        // Don't wipe `sessions`. Keep the previous page visible so a
        // retryable error doesn't feel like a wipe.
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    load(page);
    // Only initial mount + explicit refresh — subsequent page changes
    // go through `setPage` below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPage = useCallback(
    (next) => {
      const p = Math.max(1, Number(next) || 1);
      // No-op if unchanged AND we're not currently in an error state
      // (retry should re-issue the same page).
      if (p === page && !error) return;
      load(p);
    },
    [page, error, load],
  );

  const refresh = useCallback(() => load(page), [load, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    sessions,
    total,
    page,
    pageSize,
    totalPages,
    loading,
    error,
    refresh,
    setPage,
  };
}
